import React, { useState } from "react";
import type { ManifestAsset } from "../../types.js";
import { findVariantsForAsset, moveAssetToInternal, fetchBlocklist, uploadBlocklist } from "../../admin/graph.js";

interface Props {
  assets: ManifestAsset[];
  onClose: () => void;
  onComplete: (succeededIds: string[]) => void;
}

type AssetStatus = "pending" | "running" | "done" | "error";

interface AssetProgress {
  id: string;
  name: string;
  status: AssetStatus;
  message?: string;
  variantsDeleted?: number;
}

export function CommitModal({ assets, onClose, onComplete }: Props) {
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<AssetProgress[]>(() =>
    assets.map((a) => ({ id: a.id, name: a.name, status: "pending" as AssetStatus })),
  );
  const [finished, setFinished] = useState(false);
  const [setupError, setSetupError] = useState<string | null>(null);

  const succeededIds = progress.filter((p) => p.status === "done").map((p) => p.id);
  const errored = progress.filter((p) => p.status === "error");
  const doneCount = progress.filter((p) => p.status === "done" || p.status === "error").length;

  const run = async () => {
    setRunning(true);
    setSetupError(null);

    // Read the existing blocklist FIRST. This call also resolves the public +
    // private drive ids — and the private drive lives on the isolated
    // InternalAssets site. If the signed-in curator hasn't been granted access
    // to that site, this throws a 403 BEFORE any asset is touched. Catch it
    // explicitly so we surface a real reason instead of falling through to the
    // finally block and reporting a misleading "Done — 0 moved, 0 failed".
    let existingBlocklist: string[];
    try {
      existingBlocklist = await fetchBlocklist();
    } catch (e) {
      const msg = (e as Error).message;
      const accessLikely = /\b40[13]\b/.test(msg) || /InternalAssets/i.test(msg);
      setSetupError(
        (accessLikely
          ? "Couldn't start — your account doesn't appear to have access to the Internal Assets site, " +
            "so the move can't be staged. Ask IT to add you as a member of that site, then try again. "
          : "Couldn't start the move — a setup step failed before any asset was changed. ") +
          `(${msg.slice(0, 240)})`,
      );
      setRunning(false);
      return; // nothing was moved; do NOT show the "Done" summary
    }

    try {
      const newlyBlocked: string[] = [];

      for (const a of assets) {
        updateOne(setProgress, a.id, { status: "running" });
        try {
          const variants = await findVariantsForAsset(a.id);
          if (variants.length === 0) {
            // Already gone from SP — still add to blocklist so picker hides it.
            updateOne(setProgress, a.id, {
              status: "done",
              message: "Was already removed — now hidden",
              variantsDeleted: 0,
            });
            newlyBlocked.push(a.id);
            continue;
          }
          const { deletedCount } = await moveAssetToInternal(a.id, variants);
          updateOne(setProgress, a.id, {
            status: "done",
            message: "Moved to Internal Assets",
            variantsDeleted: deletedCount,
          });
          newlyBlocked.push(a.id);
        } catch (e) {
          updateOne(setProgress, a.id, {
            status: "error",
            message: (e as Error).message.slice(0, 200),
          });
        }
      }

      // Push the updated blocklist back to SP once, after the loop.
      if (newlyBlocked.length > 0) {
        try {
          await uploadBlocklist([...existingBlocklist, ...newlyBlocked]);
        } catch (e) {
          // Log to the modal but don't undo successful moves — those are
          // committed regardless. The blocklist can be retried.
          updateOne(setProgress, "__blocklist__", {
            status: "error",
            message: `Blocklist write failed: ${(e as Error).message}`,
          });
        }
      }
    } finally {
      setRunning(false);
      setFinished(true);
    }
  };

  const handleClose = () => {
    if (succeededIds.length > 0) onComplete(succeededIds);
    onClose();
  };

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true">
      <div className="modal admin-commit-modal">
        <header>
          <h2>
            {finished
              ? `Finished — ${succeededIds.length} moved${errored.length ? `, ${errored.length} couldn't be moved` : ""}`
              : running
                ? `Moving ${doneCount + 1} of ${assets.length}…`
                : `Move ${assets.length} image${assets.length === 1 ? "" : "s"} to Internal Assets?`}
          </h2>
          <button className="close" onClick={handleClose} aria-label="Close">×</button>
        </header>
        <div className="modal-body">
          {setupError && (
            <p className="admin-commit-error" role="alert">{setupError}</p>
          )}
          {!running && !finished && !setupError && (
            <p className="admin-commit-warn">
              These images will be moved to Internal Assets (a private area). They'll disappear
              from this public library right away, and any public links to them will stop working
              within about 15 minutes. A copy is kept in Internal Assets — nothing is permanently
              deleted.
            </p>
          )}
          <ul className="admin-commit-list">
            {progress.map((p) => (
              <li key={p.id} className={`admin-commit-row admin-commit-${p.status}`}>
                <span className="admin-commit-icon">{iconFor(p.status)}</span>
                <span className="admin-commit-name" title={p.name}>{p.name}</span>
                {p.message && <span className="admin-commit-msg">{p.message}</span>}
              </li>
            ))}
          </ul>
        </div>
        <footer className="admin-commit-footer">
          {!running && !finished && (
            <>
              <button className="admin-commit-cancel" onClick={onClose}>Cancel</button>
              <button className="admin-commit-go" onClick={run}>
                {setupError ? "Try again" : `Move ${assets.length} to Internal Assets`}
              </button>
            </>
          )}
          {finished && (
            <button className="admin-commit-go" onClick={handleClose}>Close</button>
          )}
        </footer>
      </div>
    </div>
  );
}

function updateOne(
  setter: React.Dispatch<React.SetStateAction<AssetProgress[]>>,
  id: string,
  patch: Partial<AssetProgress>,
) {
  setter((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
}

function iconFor(s: AssetStatus): string {
  switch (s) {
    case "pending": return "○";
    case "running": return "…";
    case "done": return "✓";
    case "error": return "✗";
  }
}
